/**
 * Author: Andrey Morozov
 * Confidential. Proprietary code. Do not distribute without authorization.
 *
 * Input.tsx — Input (services/operator-web-app).
 */

import type { InputHTMLAttributes, ReactNode } from 'react';

export interface InputProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'className'> {
  label?: string;
  error?: string;
  hint?: string;
  leftAddon?: ReactNode;
  rightAddon?: ReactNode;
  className?: string;
  inputClassName?: string;
}

export function Input({
  label,
  error,
  hint,
  leftAddon,
  rightAddon,
  className = '',
  inputClassName = '',
  id: idProp,
  ...rest
}: InputProps) {
  const id = idProp ?? `input-${Math.random().toString(36).slice(2, 9)}`;
  const baseInput =
    'block w-full rounded-lg border focus:outline-none focus:ring-2 focus:ring-sky-500 focus:border-transparent disabled:opacity-50 disabled:cursor-not-allowed transition-colors';
  const border = error ? 'border-red-500' : '';
  const padding = leftAddon || rightAddon ? 'py-2' : 'px-4 py-2';
  const withAddon = leftAddon ? 'pl-10' : rightAddon ? 'pr-10' : '';

  return (
    <div className={className}>
      {label && (
        <label
          htmlFor={id}
          className="block text-sm font-medium mb-1"
          style={{ color: 'var(--text-secondary)' }}
        >
          {label}
        </label>
      )}
      <div className="relative">
        {leftAddon && (
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" style={{ color: 'var(--text-muted)' }}>
            {leftAddon}
          </div>
        )}
        <input
          id={id}
          className={[baseInput, border, padding, withAddon, inputClassName]
            .filter(Boolean)
            .join(' ')}
          style={{
            background: 'var(--toggle-bg)',
            color: 'var(--text-primary)',
            borderColor: error ? undefined : 'var(--glass-border)',
            ...(rest.style ?? {}),
          }}
          aria-invalid={!!error}
          aria-describedby={error ? `${id}-error` : hint ? `${id}-hint` : undefined}
          {...rest}
        />
        {rightAddon && (
          <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none" style={{ color: 'var(--text-muted)' }}>
            {rightAddon}
          </div>
        )}
      </div>
      {error && (
        <p id={`${id}-error`} className="mt-1 text-sm text-red-600 dark:text-red-400" role="alert">
          {error}
        </p>
      )}
      {hint && !error && (
        <p id={`${id}-hint`} className="mt-1 text-sm" style={{ color: 'var(--text-muted)' }}>
          {hint}
        </p>
      )}
    </div>
  );
}
